import type { CSSProperties } from 'react';
import { CircleMinus, CirclePlus } from 'lucide-react';
import { useMap } from 'react-leaflet';
import css from './custom-zoom-control.module.scss';

// Custom +/- zoom buttons, stacked bottom-right above the Leaflet attribution.
// Mount as a child of `<MapContainer>` (it consumes `useMap()`); the container
// hides Leaflet's default control via `zoomControl={false}`.
//
// Props capture the two legitimate variations across hosts:
//   - `bottom` offsets the stack from the map's bottom edge. The console passes
//     the default; the public preview sits above an attribution/footer bar and
//     passes a larger value.
//   - `bordered` toggles the 1px outline. Colours themselves are identical
//     across hosts (both resolve to the `:root` light-theme tokens), so no
//     colour prop is needed.
export const CustomZoomControl = ({
  bottom = 24,
  bordered = true,
}: {
  bottom?: number;
  bordered?: boolean;
}) => {
  const map = useMap();
  const rootStyle = { bottom } satisfies CSSProperties;
  const buttonClass = bordered
    ? `${css.button} ${css.buttonBordered}`
    : css.button;
  return (
    <div className={css.root} style={rootStyle}>
      <button
        aria-label="Zoom in"
        className={buttonClass}
        onClick={() => map.zoomIn()}
        type="button"
      >
        <CirclePlus size={18} />
      </button>
      <button
        aria-label="Zoom out"
        className={buttonClass}
        onClick={() => map.zoomOut()}
        type="button"
      >
        <CircleMinus size={18} />
      </button>
    </div>
  );
};
